import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { cn } from '../../lib/cn';
import { Card, Button } from '../ui';
import HomeworkItemCard from './HomeworkItemCard';

export default function HomeworkBuilderPanel({
  homeworkActivityId,
  items: initialItems = [],
  courseActivities = [],
  lessonId,
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState(initialItems);
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [addMode, setAddMode] = useState(null); // 'activity' | 'bank'

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);

    fetch(`/teacher/lesson_activities/${homeworkActivityId}/homework_items/reorder`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ item_ids: reordered.map((i) => i.id) }),
    });
  }, [items, homeworkActivityId, csrfToken]);

  const handleRemove = async (itemId) => {
    setItems((prev) => prev.filter((i) => i.id !== itemId));

    await fetch(`/teacher/homework_items/${itemId}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken },
    });
  };

  const handleAddItem = async (sourceableType, sourceableId, instructions = null) => {
    const res = await fetch(`/teacher/lesson_activities/${homeworkActivityId}/homework_items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ sourceable_type: sourceableType, sourceable_id: sourceableId, instructions }),
    });

    if (res.ok) {
      const data = await res.json();
      setItems((prev) => [...prev, data.homework_item]);
      setShowAddPicker(false);
      setAddMode(null);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{t('homework.title')}</h4>
          <p className="text-[10px] text-gray-400 dark:text-gray-500">
            {items.length} item{items.length !== 1 ? 's' : ''}
          </p>
        </div>
        {lessonId && (
          <a
            href={`/teacher/lessons/${lessonId}/question_bank`}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {t('homework.viewBank')}
          </a>
        )}
      </div>

      {/* Item list */}
      {items.length > 0 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2 mb-3">
              {items.map((item) => (
                <SortableHomeworkItem key={item.id} item={item} onRemove={handleRemove} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4 italic mb-3">
          {t('homework.noItems')}
        </p>
      )}

      {/* Add actions */}
      {showAddPicker ? (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
          {!addMode ? (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" className="flex-1" onClick={() => setAddMode('activity')}>
                {t('homework.addActivity')}
              </Button>
              <Button variant="secondary" size="sm" className="flex-1" onClick={() => {
                if (lessonId) window.location.href = `/teacher/lessons/${lessonId}/question_bank`;
              }}>
                {t('homework.addFromBank')}
              </Button>
            </div>
          ) : addMode === 'activity' ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('homework.chooseActivity')}</p>
                <button
                  onClick={() => { setAddMode(null); setShowAddPicker(false); }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {courseActivities.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">{t('homework.noActivities')}</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {courseActivities.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => handleAddItem('Activity', a.id)}
                      className="w-full text-left p-2 rounded-md border border-gray-200 dark:border-gray-700 hover:border-gray-900 dark:hover:border-white transition-colors bg-white dark:bg-gray-900"
                    >
                      <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{a.title}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {a.question_count || 0} {t('common.questions')}
                        {a.stimulus_title && ` · ${a.stimulus_title}`}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : (
        <button
          onClick={() => setShowAddPicker(true)}
          className="w-full py-2 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center justify-center gap-1 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-400 dark:hover:border-gray-500"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('homework.addToHomework')}
        </button>
      )}
    </Card>
  );
}

// ── Sortable wrapper ──────────────────────────────────────

function SortableHomeworkItem({ item, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: transform ? `translate3d(0, ${Math.round(transform.y)}px, 0)` : undefined,
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div className={cn(isDragging && 'opacity-50 shadow-lg rounded-lg')}>
        <HomeworkItemCard item={item} onRemove={onRemove} />
      </div>
    </div>
  );
}
